import { useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, RotateCcw, Loader2, CalendarRange, Calendar, FileText, CheckCircle2, Phone } from 'lucide-react';
import { useStatsBlockGuard } from '../hooks/useStatsBlockGuard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function rangeFor(preset) {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const fmt = (d) => {
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${d.getFullYear()}-${m}-${day}`;
    };
    if (preset === 'today') return { from: fmt(today), to: fmt(today) };
    if (preset === 'yesterday') {
        const y = new Date(today); y.setDate(y.getDate() - 1);
        return { from: fmt(y), to: fmt(y) };
    }
    if (preset === 'week') {
        const d = today.getDay();
        const offset = d === 0 ? 6 : d - 1;
        const monday = new Date(today); monday.setDate(monday.getDate() - offset);
        return { from: fmt(monday), to: fmt(today) };
    }
    if (preset === 'month') {
        const first = new Date(today.getFullYear(), today.getMonth(), 1);
        return { from: fmt(first), to: fmt(today) };
    }
    if (preset === '7d') {
        const start = new Date(today); start.setDate(start.getDate() - 6);
        return { from: fmt(start), to: fmt(today) };
    }
    if (preset === '30d') {
        const start = new Date(today); start.setDate(start.getDate() - 29);
        return { from: fmt(start), to: fmt(today) };
    }
    return null;
}

const PRESET_BUTTONS = [
    { key: 'today', label: "Aujourd'hui" },
    { key: 'yesterday', label: 'Hier' },
    { key: 'week', label: 'Cette semaine' },
    { key: 'month', label: 'Ce mois' },
    { key: '7d', label: '7 jours' },
    { key: '30d', label: '30 jours' },
    { key: 'custom', label: 'Personnalisé' },
];

const STATUS_LABEL = {
    visite_presentiel: 'Visite présentiel',
    visite_virtuelle: 'Visite virtuelle',
    devis_estimatif: 'Devis estimatif',
    listing_telephone: 'Listing téléphone',
    listing_email: 'Listing email',
    listing_en_attente: 'Listing en attente',
};

export default function StatistiquesProspectsRegagnes() {
    useStatsBlockGuard('stats_regagnes');
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [preset, setPreset] = useState('month');
    const [customFrom, setCustomFrom] = useState('');
    const [customTo, setCustomTo] = useState('');

    const activeRange = useMemo(() => {
        if (preset === 'custom') return { from: customFrom, to: customTo };
        return rangeFor(preset);
    }, [preset, customFrom, customTo]);

    useEffect(() => {
        if (preset === 'custom' && (!customFrom || !customTo)) return;
        const params = new URLSearchParams();
        if (activeRange?.from) params.append('date_from', activeRange.from);
        if (activeRange?.to) params.append('date_to', activeRange.to);
        setLoading(true);
        axios.get(`${API_URL}/api/statistiques/prospects-regagnes?${params}`, { withCredentials: true })
            .then((r) => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [preset, customFrom, customTo, activeRange]);

    const totals = data?.totals || {};
    const items = data?.items || [];

    return (
        <Layout>
            <div className="space-y-6 animate-fadeIn" data-testid="stats-regagnes-page">
                {/* Header */}
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="sm" onClick={() => navigate('/statistiques')} className="h-9 px-2">
                        <ArrowLeft className="w-4 h-4" />
                    </Button>
                    <RotateCcw className="w-6 h-6 text-[#F97316]" />
                    <div>
                        <h1 className="text-xl sm:text-2xl font-bold text-[#1B365D]">Prospects regagnés</h1>
                        <p className="text-xs text-gray-500">Relances converties en visite ou devis</p>
                    </div>
                </div>

                {/* Period pills */}
                <Card className="shadow-sm">
                    <CardContent className="p-4">
                        <div className="flex flex-wrap items-center gap-2">
                            <CalendarRange className="w-4 h-4 text-[#1B365D]/60 mr-1" />
                            {PRESET_BUTTONS.map(b => (
                                <Button
                                    key={b.key}
                                    type="button"
                                    size="sm"
                                    variant={preset === b.key ? 'default' : 'outline'}
                                    onClick={() => setPreset(b.key)}
                                    className={`h-8 text-xs ${preset === b.key ? 'bg-[#F97316] hover:bg-[#ea580c] text-white' : ''}`}
                                    data-testid={`pr-preset-${b.key}`}
                                >
                                    {b.label}
                                </Button>
                            ))}
                            {preset === 'custom' && (
                                <div className="flex items-center gap-2 ml-2">
                                    <Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="h-8 text-xs w-36" data-testid="pr-custom-from" />
                                    <span className="text-xs text-gray-400">→</span>
                                    <Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="h-8 text-xs w-36" data-testid="pr-custom-to" />
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {loading ? (
                    <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#F97316]" /></div>
                ) : (
                    <>
                        {/* KPIs */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <Card className="shadow-sm border-l-4 border-l-[#F97316]">
                                <CardContent className="p-4">
                                    <p className="text-[11px] uppercase tracking-wider text-gray-500">Regagnés</p>
                                    <p className="text-3xl font-bold text-[#F97316] tabular-nums">{totals.regagnes ?? 0}</p>
                                    <p className="text-xs text-gray-400 mt-1">sur {totals.cold_total ?? 0} prospects tièdes</p>
                                </CardContent>
                            </Card>
                            <Card className="shadow-sm">
                                <CardContent className="p-4">
                                    <p className="text-[11px] uppercase tracking-wider text-gray-500">Taux de regain</p>
                                    <p className="text-3xl font-bold text-[#1B365D] tabular-nums">{totals.taux ?? 0}%</p>
                                </CardContent>
                            </Card>
                            <Card className="shadow-sm">
                                <CardContent className="p-4 flex items-start gap-2">
                                    <Calendar className="w-5 h-5 text-blue-500 mt-1 shrink-0" />
                                    <div>
                                        <p className="text-[11px] uppercase tracking-wider text-gray-500">Rendez-vous</p>
                                        <p className="text-2xl font-bold text-[#1B365D] tabular-nums">{totals.rendez_vous ?? 0}</p>
                                    </div>
                                </CardContent>
                            </Card>
                            <Card className="shadow-sm">
                                <CardContent className="p-4 flex items-start gap-2">
                                    <FileText className="w-5 h-5 text-violet-500 mt-1 shrink-0" />
                                    <div>
                                        <p className="text-[11px] uppercase tracking-wider text-gray-500">Devis créés</p>
                                        <p className="text-2xl font-bold text-[#1B365D] tabular-nums">{totals.devis ?? 0}</p>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Accepted highlight */}
                        {totals.accepte > 0 && (
                            <Card className="shadow-sm bg-emerald-50/50 border-emerald-200">
                                <CardContent className="p-4 flex items-center gap-3">
                                    <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                                    <div>
                                        <p className="text-sm font-semibold text-emerald-700">
                                            {totals.accepte} prospect{totals.accepte > 1 ? 's' : ''} regagné{totals.accepte > 1 ? 's' : ''} devenu{totals.accepte > 1 ? 's' : ''} client{totals.accepte > 1 ? 's' : ''}
                                        </p>
                                        <p className="text-xs text-emerald-600/80">Cycle complet : tiède → relancé → accepté</p>
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* List */}
                        <Card className="shadow-sm">
                            <CardHeader className="pb-3">
                                <CardTitle className="text-base text-[#1B365D]">Liste des prospects regagnés ({items.length})</CardTitle>
                            </CardHeader>
                            <CardContent className="p-0">
                                {items.length === 0 ? (
                                    <p className="text-center text-sm text-gray-400 py-12">Aucun prospect regagné sur cette période</p>
                                ) : (
                                    <ul className="divide-y divide-gray-100">
                                        {items.map((it) => (
                                            <li
                                                key={it.prospect_id}
                                                className="px-5 py-3 flex items-center justify-between gap-3 hover:bg-[#F97316]/5 cursor-pointer transition-colors"
                                                onClick={() => navigate(`/prospects/${it.prospect_id}`)}
                                                data-testid={`pr-item-${it.prospect_id}`}
                                            >
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-medium text-sm text-[#1B365D] truncate">{it.nom || '(Sans nom)'}</p>
                                                    {it.telephone && (
                                                        <p className="text-xs text-gray-400 flex items-center gap-1">
                                                            <Phone className="w-3 h-3" />{it.telephone}
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="text-right shrink-0">
                                                    <p className="text-xs font-medium text-[#F97316]">→ {STATUS_LABEL[it.recovered_to] || it.recovered_to}</p>
                                                    <p className="text-[10px] text-gray-400">{it.recovery_date ? new Date(it.recovery_date).toLocaleDateString('fr-FR') : ''}</p>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </CardContent>
                        </Card>
                    </>
                )}
            </div>
        </Layout>
    );
}
